import { computed, type MaybeRefOrGetter } from 'vue';
import { useQueryClient } from '@tanstack/vue-query';
import {
  getGetApiV1UserClaimsQueryKey,
  getGetApiV1UserClaimsQueryOptions,
  useGetApiV1UserClaims,
} from '@services/identity-api/orval-api/user-api/user-api';
import { CommonDomainMarktsegment as Marktsegment } from '@services/identity-api/orval-api/model/commonDomainMarktsegment';
import { type IdentityWebModelsUserUserClaimDto } from '@services/identity-api/orval-api/model/identityWebModelsUserUserClaimDto';
import { type FlattenedPermissions } from '@permission/types';

function hasPermission(
  claims: IdentityWebModelsUserUserClaimDto[],
  permission: FlattenedPermissions,
): boolean {
  return claims.some((t) => t.type === 'Permission' && t.value === permission);
}

function buildClaimsObject(
  claims: IdentityWebModelsUserUserClaimDto[] | undefined,
  isLoading: boolean,
  isSuccess: boolean,
) {
  const checkClaim = (type: string) =>
    claims?.some((d) => d.type === type && d.value === 'True') ?? false;

  const marktsegment = claims?.find((d) => d.type === 'Marktsegment')?.value as
    | Marktsegment
    | undefined;

  return {
    isInitialLoading: isLoading,
    magBeheerboxInzien: hasPermission(claims ?? [], 'Inregeling.InregelingInzien'),
    magAdviseurbeheerInzien: hasPermission(claims ?? [], 'Crm.Adviseurbeheer'),
    isSuccess,
    isWebmodule: claims?.some((d) => d.type === 'WebmoduleId' && d.value !== '0') ?? false,
    isTemporaryUser: checkClaim('IsTemporaryUser'),
    isInternal: checkClaim('IsInternal'),
    isFinancieel: checkClaim('HeeftFinancieelPermissie'),
    heeftInstellingenPermissie: checkClaim('HeeftInstellingenPermissie'),
    heeftWebmodulePermissie: checkClaim('HeeftWebmodulePermissie'),
    isMaatschappij: checkClaim('IsMaatschappij'),
    isAdviseur: checkClaim('IsAdviseur'),
    marktsegment,
    isParticulier: marktsegment === Marktsegment.Particulier,
    webmoduleId: claims?.some((d) => d.type === 'WebmoduleId')
      ? Number(claims?.filter((d) => d.type === 'WebmoduleId')[0].value)
      : undefined,
  };
}

export function useClaims(enabled: MaybeRefOrGetter<boolean | undefined> = undefined) {
  const {
    data: claims,
    isLoading,
    isSuccess,
  } = useGetApiV1UserClaims({
    query: {
      staleTime: 60 * 1000,
      retry: 10,
      enabled,
    },
  });

  return computed(() => buildClaimsObject(claims.value, isLoading.value, isSuccess.value));
}

export async function useClaimsAwaitable() {
  const client = useQueryClient();
  const claims = await client.ensureQueryData(
    getGetApiV1UserClaimsQueryOptions({
      query: { staleTime: 60 * 1000, retry: 10 },
    }),
  );
  return buildClaimsObject(claims, false, true);
}

export function useRefreshClaims(): { refresh: () => Promise<void> } {
  const queryClient = useQueryClient();
  return {
    refresh: () => queryClient.invalidateQueries({ queryKey: getGetApiV1UserClaimsQueryKey() }),
  };
}
